import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Card, CardContent, Grid2, Typography } from '@mui/material';
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-pro";
import { DataGridPro, DataGridProProps } from '@mui/x-data-grid-pro';
import { format } from 'date-fns';
import useApi from '../hooks/useApi';
import { dataGridHeaderHeight, dataGridRowHeight, GLOBALSETTINGS, GRIDCOLUMNKEYS, ModuleNames, SEARCHBOX, SYSTEMSETTINGS } from '../data/constants';
import { AuditTraceRequest } from '../types/AuditLog';
import { AuditNavigationTrace } from '../types/AuditNavigationTrace';
import { useUser } from '../context/UserContext';
import { getNavigationTraces } from '../services/auditNavigationTraceService';
import SearchBox from '../components/form/SearchBox/SearchBox';
import { useSettingValue } from '../services/useSettingValue';
import { filterDataBySearch, handleScrollPositionChange } from '../utils/commonHelper';
import CustomLoadingOverlay from '../components/ui/CustomLoadingOverlay';
import { useMenu } from '../context/MenuContext';
import UsersDropdownList from '../components/ui/UsersDropdownList';
import { GLOBALMESSAGES } from '../constants/messages';
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePersistedColumnVisibility } from "../hooks/usePersistedColumnVisibility";
const Activities: React.FC = () => {
  const [searchResult, setSearchResult] = useState('');
  const { loading, executeApi } = useApi();
  const settingValue = useSettingValue(SYSTEMSETTINGS.AUDITTRACE_PAGINATION);
  const [isPaginationInitialized, setPaginationInitialized] = useState(false);
  const [pageState, setPageState] = useState<{ data: AuditNavigationTrace[] | null; total: number }>({
    data: null,
    total: 0,
  });
  const [isClientSearchEvaluated, setIsClientSearchEvaluated] = useState(false);
  const [allData, setAllData] = useState<AuditNavigationTrace[]>([]);
  const [handleGoTrigger, setHandleGoTrigger] = useState(-1);
  const isFetching = useRef(false);
  const isFirstLoaded = useRef(false);
  const mounted = React.useRef(true);
  const apiRef = useGridApiRef();
  const { userId } = useUser();
  const { menu } = useMenu();


  const enableAllUsers = menu?.find(section =>
    section.listItems?.some(item => item.moduleName === ModuleNames.UsersActivities)
  );

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10),
  });

  const prevPagination = useRef(paginationModel);
  const prevSearchResult = useRef(searchResult);
  const selectedUsers = useRef<number[]>([userId]);

  const handleUserChange = (selectedUserIds: number[]) => {
    selectedUsers.current = selectedUserIds ?? [];
    setHandleGoTrigger((prev) => prev + 1);
  };

  React.useEffect(() => {
    apiRef?.current?.subscribeEvent('scrollPositionChange', handleScrollPositionChange);
    return () => {
      mounted.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnRowsScrollEnd: DataGridProProps['onRowsScrollEnd'] = () => {
    if (loading || !pageState?.data) return; // Avoid multiple triggers
    if (isFetching.current) return;

    if (pageState?.data && pageState?.data.length >= pageState?.total) {
      return;
    }

    isFetching.current = true;
    setPaginationModel((prev) => {
      const totalPages = Math.ceil(pageState?.total / prev.pageSize);
      if (prev.page + 1 < totalPages) {
        return { ...prev, page: prev.page + 1 };
      }
      return prev;
    });

    // Restore previous scroll position after data updates
    setTimeout(() => {
      isFetching.current = false;
    }, 100);
  };

  const loadData = useCallback(async () => {
    const isNewSearch = paginationModel.page === 0;
    if (isClientSearchEvaluated && isFirstLoaded.current) {
      if (searchResult) {
        const lowerText = searchResult.toLowerCase();
        const filtered = filterDataBySearch(allData, lowerText);
        setPageState({
          data: filtered ?? [],
          total: filtered?.length,
        });
      } else {
        // If no search term, reset to full data
        setPageState({
          data: allData ?? [],
          total: allData?.length,
        });

      }
    }
    else {

      const auditTraceRequest: AuditTraceRequest = {
        searchTerm: searchResult,
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        userIds: enableAllUsers ? (selectedUsers.current ?? []) : [userId]
      }
      const auditTraceData = await executeApi<AuditTraceRequest, AuditNavigationTrace[]>(getNavigationTraces, auditTraceRequest);
      if (auditTraceData && auditTraceData.length > 0) {
        if (!isFirstLoaded.current && auditTraceData[0]?.totalRecords <= paginationModel.pageSize) {
          setIsClientSearchEvaluated(true);
          setAllData(auditTraceData);
        }
        isFirstLoaded.current = true;

        setPageState((prev) => {
          const existingIds = new Set(prev.data?.map((row) => row.navigationId)); // Track existing IDs
          const newData = auditTraceData.filter((row) => !existingIds.has(row.navigationId)); // Filter duplicates

          return {
            data: isNewSearch ? auditTraceData : [...(prev.data || []), ...newData],
            total: auditTraceData.length > 0 && isNewSearch ? auditTraceData[0].totalRecords : prev.total,
          };
        });
      }
      else {
        setPageState({
          data: [],
          total: 0,
        });
      }
    }
    setTimeout(() => {
      apiRef?.current?.scrollToIndexes({ rowIndex: apiRef?.current?.getScrollPosition()?.top });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, paginationModel, searchResult, executeApi]);

  useEffect(() => {
    if (settingValue) {
      const newPageSize = parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10);

      setPaginationModel((prev) => {
        if (prev.pageSize !== newPageSize) {
          return { page: 0, pageSize: newPageSize };
        }
        return prev;
      });
      setPaginationInitialized(true);
    }
  }, [settingValue]);

  useEffect(() => {
    if (isPaginationInitialized && enableAllUsers && handleGoTrigger > -1) {
      setIsClientSearchEvaluated(false);
      isFirstLoaded.current = false;
      if (prevPagination.current.page === 0) {
        loadData();
      }
      else {
        setPaginationModel((prev) => ({
          ...prev,
          page: 0,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleGoTrigger])

  useEffect(() => {
    const hasChanged = JSON.stringify(prevPagination.current) !== JSON.stringify(paginationModel);
    const searchChanged = JSON.stringify(prevSearchResult.current) !== JSON.stringify(searchResult);
    if (isPaginationInitialized || hasChanged || searchChanged) {
      prevPagination.current = paginationModel;
      prevSearchResult.current = searchResult;
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationInitialized, paginationModel, searchResult])



  const handleSearchText = useCallback((text: string) => {
    setSearchResult((prev) => (prev !== text ? text : prev));
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
    }));
  }, []);

  const columns: GridColDef[] = [
    { field: 'userName', headerName: 'User', flex: 1, minWidth: 100 },
    {
      field: 'navigationDateTime', headerName: 'Date Time', flex: 1, minWidth: 180,
      valueFormatter: (value) => value ? format(value, GLOBALSETTINGS.DATE_FORMAT_TIME) : ""
    },
    { field: 'objectType', headerName: 'Object Type', flex: 1, minWidth: 100 },
    { field: 'objectName', headerName: 'Object Name', flex: 3, minWidth: 250 },
    { field: 'action', headerName: 'Action', flex: 1, minWidth: 150, hideable: false, filterable: false,sortable: false, disableColumnMenu: false}
  ];

    const {
      columnState,
      handleColumnResize,
    } = useResizableColumns(columns);

  const { columnVisibilityModel, setColumnVisibilityModel } = usePersistedColumnVisibility(
    GRIDCOLUMNKEYS.ACTIVITIES
  );

    
  return (
    <section className='mb-3'>
      <Card>
        <CardContent>
          <Typography variant="h5" component="div" className="mb-3 card-title">Activities</Typography>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Grid2 container spacing={2} width="100%" alignItems="center">
              {enableAllUsers && (<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <Box>
                  <UsersDropdownList userId={userId} controlHeight={40} onUserChange={handleUserChange} debounceChange={true} />
                </Box>
                
              </Grid2>)}

              <Grid2 size={{ xs: 12, sm: 6, md: 9 }}>
                <Box width="100%" className="search-box-wrap">
                  <SearchBox searchPlaceholder={SEARCHBOX.TEXT_PLACEHOLDER} onSearch={handleSearchText} />
                  </Box>
              </Grid2>

            </Grid2>

            <Box width="100%">
              <div style={{ height: 500, width: '100%' }}>
                <DataGridPro
                  apiRef={apiRef}
                  rowHeight={dataGridRowHeight}
                  rows={pageState?.data ?? []}
                  {...pageState?.data}
                  columns={columnState}
                  rowCount={pageState?.total}
                  columnHeaderHeight={dataGridHeaderHeight}
                  slots={{
                    loadingOverlay: CustomLoadingOverlay,
                  }}
                  loading={loading}
                  paginationMode="server"
                  getRowId={(row) => row.navigationId}
                  hideFooterPagination
                  onRowsScrollEnd={handleOnRowsScrollEnd}
                  localeText={{ noRowsLabel: GLOBALMESSAGES.NOROWSTEXT }}
                  onColumnWidthChange={(params) => handleColumnResize(params.width, params.colDef)}
                  columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityModelChange={setColumnVisibilityModel}
                />
              </div>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </section>
  );
};

export default Activities;
